import { useEffect, useMemo, useState } from "react";
import { Camera, LayoutGrid, Loader2, Orbit } from "lucide-react";
import { COLLECTION_CATEGORIES } from "@/data/catalog";
import { getJson, mediaUrl } from "@/lib/api";
import { locDoc, useLang } from "@/lib/i18n";
import { MaskLines } from "@/components/MaskLines";
import { PhotoViewer } from "@/components/PhotoViewer";
import { Tunnel } from "@/components/collection/Tunnel";

const Chip = ({ on, label, count, onClick, testid }) => (
  <button type="button" onClick={onClick} className={`col-chip ${on ? "col-chip-on" : ""}`} data-testid={testid}>
    {label} {count !== undefined && <span className="mono text-[10px] opacity-70">{count}</span>}
  </button>
);

const Tile = ({ p, onOpen }) => {
  const { t } = useLang();
  return (
    <button type="button" onClick={onOpen} className="col-tile" data-testid={`collection-photo-${p.id}`}>
      <img src={p.src} alt={p.title || p.description || t("col.photoAlt")} loading="lazy" />
      <span className="col-tile-cap">
        <span className="mono flex flex-wrap gap-x-2 text-[10px] uppercase tracking-[0.14em] text-[var(--green)]">
          <span>{t(`col.${p.category}`)}</span>{p.year && <span>· {p.year}</span>}{p.universe && <span>· {p.universe}</span>}
        </span>
        {p.title && <span className="font-display mt-1 block text-xl leading-tight">{p.title}</span>}
        {p.description && <span className="mt-1.5 block text-sm leading-snug text-[#cfc7b9]">{p.description}</span>}
      </span>
    </button>
  );
};

const ModeToggle = ({ mode, setMode }) => {
  const { t } = useLang();
  return (
    <div className="inline-flex rounded-full border border-white/15 bg-white/5 p-1" data-testid="collection-mode-toggle">
      {[["tunel", t("col.tunnel"), Orbit], ["mural", t("col.mural"), LayoutGrid]].map(([k, l, I]) => (
        <button key={k} type="button" onClick={() => setMode(k)} className={`col-mode ${mode === k ? "col-mode-on" : ""}`} data-testid={`collection-mode-${k}`}><I size={15} /> {l}</button>
      ))}
    </div>
  );
};

const countBy = (arr, key) => arr.reduce((m, p) => (p[key] ? { ...m, [p[key]]: (m[p[key]] ?? 0) + 1 } : m), {});

export default function Collection() {
  const { t, lang } = useLang();
  const [rows, setRows] = useState(null);
  const [cat, setCat] = useState(null);
  const [uni, setUni] = useState(null);
  const [mode, setMode] = useState("tunel");
  const [open, setOpen] = useState(null);
  useEffect(() => { getJson("/collection").then((d) => setRows(Array.isArray(d) ? d : [])); }, []);
  const photos = useMemo(() => (rows ?? []).map((raw) => {
    const r = locDoc(raw, lang);
    const caption = [r.title, t(`col.${r.category}`), r.year, r.universe, r.number && `No. ${r.number}`, r.country, r.language].filter(Boolean).join(" · ");
    return { ...r, universe: raw.universe, src: mediaUrl(r.image_url), caption };
  }), [rows, lang, t]);
  const shown = photos.filter((p) => (!cat || p.category === cat) && (!uni || p.universe === uni));
  const cats = countBy(photos, "category");
  const unis = countBy(photos, "universe");
  return (
    <main className="pb-28 pt-32" data-testid="collection-page">
      <div className="mx-auto max-w-6xl px-5 md:px-10">
        <p className="mono text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]">{t("nav.collection")}</p>
        <MaskLines as="h1" className="font-display mt-4 text-5xl leading-[0.95] sm:text-6xl lg:text-7xl" lines={[t("col.l1"), <em key="e">{t("col.l2")}</em>]} />
        <p className="mt-6 max-w-2xl text-sm text-[var(--muted)] md:text-base">{t("col.intro")}</p>
        <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
          <ModeToggle mode={mode} setMode={setMode} />
          <span className="mono text-[11px] text-[var(--muted)]" data-testid="collection-count">{t(shown.length === 1 ? "col.photo1" : "col.photos", { n: shown.length })}</span>
        </div>
        <div className="mt-5 flex flex-wrap gap-2" data-testid="collection-filters">
          <Chip on={!cat} label={t("col.all")} count={photos.length} onClick={() => setCat(null)} testid="collection-cat-all" />
          {COLLECTION_CATEGORIES.filter(([k]) => cats[k]).map(([k]) => <Chip key={k} on={cat === k} label={t(`col.${k}`)} count={cats[k]} onClick={() => setCat(k)} testid={`collection-cat-${k}`} />)}
        </div>
        {Object.keys(unis).length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="collection-universe-filters">
            <span className="mono mr-1 text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">{t("col.universe")}</span>
            <Chip on={!uni} label={t("col.allU")} onClick={() => setUni(null)} testid="collection-uni-all" />
            {Object.keys(unis).sort().map((u) => <Chip key={u} on={uni === u} label={u} count={unis[u]} onClick={() => setUni(u)} testid={`collection-uni-${u.replace(/\W+/g, "-").toLowerCase()}`} />)}
          </div>
        )}
        {rows === null && <Loader2 className="mx-auto mt-20 animate-spin" />}
        {rows && !shown.length && (
          <div className="mt-12 grid place-items-center rounded-2xl border border-dashed border-white/20 px-6 py-20 text-center" data-testid="collection-empty">
            <Camera size={22} className="text-[var(--muted)]" />
            <p className="font-display mt-4 text-3xl">{t("col.soon")}</p>
            <p className="mt-2 text-sm text-[var(--muted)]">{t("col.soonBody")}</p>
          </div>
        )}
      </div>
      {shown.length > 0 && mode === "tunel" && <div className="mt-10"><Tunnel key={`${cat}-${uni}`} photos={shown} onOpen={setOpen} /></div>}
      {shown.length > 0 && mode === "mural" && (
        <div className="mx-auto mt-10 max-w-6xl columns-1 gap-5 px-5 sm:columns-2 md:px-10 lg:columns-3" data-testid="collection-mural">
          {shown.map((p, i) => <Tile key={p.id} p={p} onOpen={() => setOpen(i)} />)}
        </div>
      )}
      <PhotoViewer photos={shown} index={open} setIndex={setOpen} />
    </main>
  );
}
